import * as React from 'react';
import { Button, Dropdown, Popup } from 'semantic-grab-ui/src';
import _ from 'lodash';
import { ConditionWidget, ActionWidget, RandomizerWidget, ExperimentWidget } from 'components/form/trident-campaigns';
import { getNodePopupContent, getDefaultNodeName } from 'omega-rtc/utils/trident-node-utils';
import './FlowDesign.scss';
import { preOrderTraversalFromCurrentNodeToItsChildren, renderDropdownItem, getNodeTypeChildOptions } from './util';
import { CampaignWidgetProps } from 'types/ts/campaigns';
import { nodeTypes } from 'data/trident-types';

export default class DelayWidget extends React.Component<CampaignWidgetProps> {
  getDelayNodeChildOptions = (userType: string, nodeType: string, ancestorList: string[]) => {
    let childOptions = getNodeTypeChildOptions(userType, nodeType);

    // 1. If ancestorList length > 2, return existing childOptions
    if (ancestorList.length > 2) {
      return childOptions;
    }

    // 2. If ancestorList length is 2, check node types
    if (ancestorList.length === 2) {
      const firstNodeKey = ancestorList[0];
      const secondNodeKey = ancestorList[1];
      const { model } = this.props;

      const firstNodeType = _.get(model.nodes[firstNodeKey], 'type', '');
      const secondNodeType = _.get(model.nodes[secondNodeKey], 'type', '');
      const firstNodeScenarios = _.get(model.nodes[firstNodeKey], 'data.scenarios', []);

      // If first node is single scenario node, and second node is delay, add condition to childOptions
      if (firstNodeType === nodeTypes.scenarios &&
          firstNodeScenarios.length === 1 &&
          secondNodeType === nodeTypes.delay) {
        childOptions = [...childOptions, nodeTypes.condition];
      }
    }

    return childOptions;
  }

  render() {
    const { model, canActivate, currentNodeKey, parentKeyList } = this.props;
    const delayButtonStyle = {
      height: 60,
      width: 150,
      backgroundColor: '#D0ECE7',
      borderRadius: '0 999px 999px 0',
      borderCornerShape: 'bevel',
      border: '1px solid #76D7C4',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
    const delayButtonStyleClicked = {
      ...delayButtonStyle,
      backgroundColor: '#76D7C4',
      border: '1px solid #148F77',
    };
    const addButtonSize = {
      fontSize: '1.5em',
      marginTop: '20px',
      color: '#3498DB',
    };
    const noOfChildren = _.get(model.nodes[currentNodeKey], 'children', []).length;
    const renderAddButton = noOfChildren === 0;

    // check wheather flow is completed or not, it will help to render add button with set of options after flow completed.
    const flowCompleted = preOrderTraversalFromCurrentNodeToItsChildren(model, currentNodeKey, true);
    const addButtonAfterFlow = noOfChildren !== 0 && flowCompleted;
    // preparing ancestor list of current clicked node to send it into node preview display.
    const ancestorList = _.concat(parentKeyList, currentNodeKey);
    const parentKey = parentKeyList[parentKeyList.length - 1];
    const defaultName = getDefaultNodeName(currentNodeKey, nodeTypes.delay, _.get(model, 'nodesOccMap'));
    const childOptions = this.getDelayNodeChildOptions(model.targetUserType, nodeTypes.delay, ancestorList);

    return (
      <div className={_.get(this.props.model.nodes[parentKey], 'children', []).length > 1 ? 'entry' : 'entry sole'}>
        <span className="wrapDesign">
          <Popup
            trigger={
              <Button style={this.props.clickedNode === this.props.currentNodeKey ? delayButtonStyleClicked : delayButtonStyle} onClick={() => this.props.handleNodePreview(ancestorList, currentNodeKey)}>
                {_.get(model.nodes[currentNodeKey], 'name', defaultName) || defaultName}
              </Button>
            }
            content={getNodePopupContent(model, 'name', currentNodeKey, defaultName)}
          />
          {(renderAddButton || addButtonAfterFlow) &&
            <Dropdown icon="plus square" floating basic className="icon" style={addButtonSize}>
              <Dropdown.Menu>
                {childOptions.map(option =>
                  renderDropdownItem(`Add ${option}`, () => this.props.handleAddNode(option, currentNodeKey, ancestorList))
                )}
              </Dropdown.Menu>
            </Dropdown>
          }
        </span>
        {_.get(model.nodes[currentNodeKey], 'children', []).length > 0 &&
          <div className={renderAddButton || addButtonAfterFlow ? 'dropdownButton' : 'noDropdownButton'}>
            {_.get(model.nodes[currentNodeKey], 'children', []).map((childKey, index) => {
              if (model.nodes[childKey].type === 'condition') {
                return (
                  <ConditionWidget
                    key={`${index}${_.get(model.nodes[childKey], 'type')}`}
                    onModelChange={this.props.onModelChange}
                    model={model}
                    currentNodeKey={childKey}
                    canActivate={canActivate}
                    parentKeyList={ancestorList}
                    handleAddNode={this.props.handleAddNode}
                    handleNodePreview={this.props.handleNodePreview}
                    clickedNode={this.props.clickedNode}
                  />
                );
              }
              if (model.nodes[childKey].type === 'randomizer') {
                return (
                  <RandomizerWidget
                    key={`${index}${_.get(model.nodes[childKey], 'type')}`}
                    model={model}
                    canActivate={canActivate}
                    currentNodeKey={childKey}
                    parentKeyList={ancestorList}
                    onModelChange={this.props.onModelChange}
                    handleAddNode={this.props.handleAddNode}
                    handleNodePreview={this.props.handleNodePreview}
                    clickedNode={this.props.clickedNode}
                  />
                );
              }
              if (model.nodes[childKey].type === 'experiment') {
                return (
                  <ExperimentWidget
                    key={`${index}${_.get(model.nodes[childKey], 'type')}`}
                    model={model}
                    canActivate={canActivate}
                    currentNodeKey={childKey}
                    parentKeyList={ancestorList}
                    onModelChange={this.props.onModelChange}
                    handleAddNode={this.props.handleAddNode}
                    handleNodePreview={this.props.handleNodePreview}
                    clickedNode={this.props.clickedNode}
                  />
                );
              }
              return (
                <ActionWidget
                  key={`${index}${_.get(model.nodes[childKey], 'type')}`}
                  onModelChange={this.props.onModelChange}
                  model={model}
                  currentNodeKey={childKey}
                  canActivate={canActivate}
                  parentKeyList={ancestorList}
                  handleNodePreview={this.props.handleNodePreview}
                  clickedNode={this.props.clickedNode}
                />
              );
            })}
          </div>
        }
      </div>
    );
  }
}
